import {
  Component,
  OnInit,
  OnDestroy
} from "@angular/core";

import {
  FormGroup,
  FormControl,
  Validators
} from "@angular/forms";

import {
  ActivatedRoute,
  Router
} from "@angular/router";

import { Subscription } from "rxjs";

import { SmartContractService } from "../smart-contract-management.service";
import { KeycloakStateService } from "src/app/core/service/utility.service";
import { MatDialog } from "@angular/material/dialog";

import { SuccessDialogComponent } from "../../shared/dialogs/success-dialog/success-dialog.component";
import { ErrorDialogComponent } from "../../shared/dialogs/error-dialog.component";

@Component({
  selector: "app-smart-contract-create-or-update",
  templateUrl: "./smart-contract-management-create-or-update.component.html",
  styleUrls: ["./smart-contract-management-create-or-update.component.scss"]
})
export class SmartContractManagementCreateOrUpdateComponent
  implements OnInit, OnDestroy {

  form!: FormGroup;

  isCreate = true;
  selectedFile!: File;

  loading = false;
  statusMessage = "Starting...";

  isDragging = false;

  private pollingTimer: ReturnType<typeof setTimeout> | null = null;
  private statusSubscription: Subscription | null = null;

  private pollingStopped = true;
  private activeJobId: string | null = null;

  private readonly terminalStatuses = new Set<string>([
    "DEPLOYED",
    "UPGRADED",
    "DELETED",
    "FAILED",
    "UPGRADE_FAILED",
    "DELETE_FAILED",
    "CANCELLED",
    "CANCELED"
  ]);

  constructor(
    private service: SmartContractService,
    private keycloak: KeycloakStateService,
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.isCreate = this.route.snapshot.data["create"];

    this.form = new FormGroup({
      name: new FormControl(null, Validators.required),
      channel: new FormControl(null, Validators.required),
      version: new FormControl(null, Validators.required),
      sequence: new FormControl(null, Validators.required),
      organization: new FormControl(null, Validators.required),
      file: new FormControl(null, Validators.required)
    });

    if (!this.isCreate) {
      const id = this.route.snapshot.paramMap.get("id");

      if (id) {
        this.load(id);
      }

      this.form.get("file")?.clearValidators();
      this.form.get("file")?.updateValueAndValidity();
    }
  }

  ngOnDestroy(): void {
    this.stopPolling();

    // Cancels any outstanding HTTP subscription owned by this component.
    this.statusSubscription?.unsubscribe();
  }

  // ---------------------------------------------------------
  // LOAD CONTRACT
  // ---------------------------------------------------------

  load(id: string): void {
    this.service.getContractById(id).subscribe({
      next: (data: any) => {
        console.log("FINAL DATA:", data);

        if (!data) {
          console.error("No data found");
          return;
        }

        this.form.patchValue({
          name: data.chaincodeName,
          channel: data.channelName,
          version: data.version,
          sequence: data.sequence,
          organization: data.organization
        });
      },
      error: (error) => {
        console.error("Failed to load contract:", error);
        this.openErrorDialog("Failed to load smart contract");
      }
    });
  }

  // ---------------------------------------------------------
  // FILE HANDLING
  // ---------------------------------------------------------

  onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (file && file.name.toLowerCase().endsWith(".go")) {
      this.selectedFile = file;

      this.form.patchValue({
        file
      });

      this.form.get("file")?.updateValueAndValidity();
    } else {
      this.openErrorDialog("Only .go files are allowed");
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;

    if (!event.dataTransfer?.files.length) {
      return;
    }

    const file = event.dataTransfer.files[0];

    if (file.name.toLowerCase().endsWith(".go")) {
      this.selectedFile = file;

      this.form.patchValue({
        file
      });

      this.form.get("file")?.updateValueAndValidity();
    } else {
      this.openErrorDialog("Only .go files are allowed");
    }
  }

  // ---------------------------------------------------------
  // ORGANIZATION
  // ---------------------------------------------------------

  getOrganizationId(): string {
    return this.keycloak.getOrganizationId();
  }

  // ---------------------------------------------------------
  // SUBMIT DEPLOYMENT / UPDATE
  // ---------------------------------------------------------

  submit(): void {
    if (this.loading) {
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.openErrorDialog("Please complete all required fields");
      return;
    }

    if (this.isCreate && !this.selectedFile) {
      this.openErrorDialog("File is required for create");
      return;
    }

    const dto = {
      chaincodeName: this.form.value.name,
      channelName: this.form.value.channel,
      organizationId: this.getOrganizationId(),
      organization: this.form.value.organization,
      version: this.form.value.version,
      sequence: Number(this.form.value.sequence)
    };

    this.loading = true;
    this.statusMessage = this.isCreate
      ? "Submitting deployment..."
      : "Submitting update...";

    const request$ = this.isCreate
      ? this.service.createContract(this.selectedFile, dto)
      : this.service.updateContract(this.selectedFile ?? null, dto);

    request$.subscribe({
      next: (res: any) => {
        const jobId = res?.jobId;

        if (!jobId || typeof jobId !== "string") {
          this.loading = false;
          this.statusMessage = "Invalid job response";

          console.error("Missing jobId in operation response:", res);

          this.openErrorDialog(
            "The server accepted no identifiable job. Please check the backend response."
          );

          return;
        }

        console.log("Operation accepted. Job ID:", jobId);

        this.statusMessage = "Deployment accepted. Waiting for completion...";

        this.pollJob(jobId);
      },

      error: (error) => {
        this.loading = false;

        console.error("Failed to submit chaincode operation:", error);

        this.statusMessage = "Failed to submit operation";

        this.openErrorDialog(
          "Failed to submit the smart contract operation"
        );
      }
    });
  }

  // ---------------------------------------------------------
  // JOB STATUS POLLING
  // ---------------------------------------------------------

  pollJob(jobId: string): void {
    // Avoid duplicate polling loops.
    this.stopPolling();

    this.activeJobId = jobId;
    this.pollingStopped = false;

    console.log("Polling started for job:", jobId);

    // Request status immediately instead of waiting three seconds.
    this.checkJobStatus(jobId);
  }

  private checkJobStatus(jobId: string): void {
    if (this.pollingStopped || this.activeJobId !== jobId) {
      return;
    }

    // Never leave an older request active while starting another one.
    this.statusSubscription?.unsubscribe();

    this.statusSubscription = this.service.getStatus(jobId).subscribe({
      next: (res: any) => {
        if (this.pollingStopped || this.activeJobId !== jobId) {
          return;
        }

        const status = String(res?.status ?? "")
          .trim()
          .toUpperCase();

        console.log(
          "Received chaincode status:",
          status,
          "jobId:",
          jobId
        );

        if (!status) {
          this.scheduleNextPoll(jobId);
          return;
        }

        this.statusMessage = `Status: ${status}`;

        // Stop polling when the backend reports a terminal state.
        if (this.terminalStatuses.has(status)) {
          this.stopPolling();

          this.loading = false;

          if (status === "DEPLOYED" || status === "UPGRADED") {
            this.statusMessage = "Deployment successful";
            this.openSuccessDialog();
            return;
          }

          if (status === "DELETED") {
            this.statusMessage = "Chaincode deleted successfully";
            this.openSuccessDialog();
            return;
          }

          this.statusMessage = `Chaincode operation failed: ${status}`;

          this.openErrorDialog(
            `Chaincode operation failed. Status: ${status}`
          );

          return;
        }

        // Nonterminal states, for example:
        // IN_PROGRESS, DEPLOYING, UPDATING, DELETING.
        this.scheduleNextPoll(jobId);
      },

      error: (error) => {
        if (this.pollingStopped || this.activeJobId !== jobId) {
          return;
        }

        const httpStatus = error?.status;

        console.error(
          "Chaincode status request failed:",
          httpStatus,
          error
        );

        if (httpStatus === 404) {
          // The job does not exist in the backend job store.
          // Stop rather than requesting the same missing job repeatedly.
          this.stopPolling();

          this.loading = false;
          this.statusMessage = "Job not found";

          this.openErrorDialog(
            "The chaincode job was not found. It may have expired, the server may have restarted, or the job ID may be incorrect."
          );

          return;
        }

        if (httpStatus >= 400 && httpStatus < 500) {
          // Permanent client errors should not be retried indefinitely.
          this.stopPolling();

          this.loading = false;
          this.statusMessage = "Unable to retrieve job status";

          this.openErrorDialog(
            `Unable to retrieve job status. HTTP ${httpStatus}.`
          );

          return;
        }

        /*
         * For a temporary network or server failure, retry after a delay.
         * Do not start overlapping requests.
         */
        this.statusMessage = "Status temporarily unavailable. Retrying...";
        this.scheduleNextPoll(jobId);
      }
    });
  }

  private scheduleNextPoll(jobId: string): void {
    if (this.pollingStopped || this.activeJobId !== jobId) {
      return;
    }

    if (this.pollingTimer) {
      clearTimeout(this.pollingTimer);
    }

    this.pollingTimer = setTimeout(() => {
      this.pollingTimer = null;
      this.checkJobStatus(jobId);
    }, 3000);
  }

  private stopPolling(): void {
    this.pollingStopped = true;
    this.activeJobId = null;

    if (this.pollingTimer) {
      clearTimeout(this.pollingTimer);
      this.pollingTimer = null;
    }

    this.statusSubscription?.unsubscribe();
    this.statusSubscription = null;
  }

  // ---------------------------------------------------------
  // NAVIGATION
  // ---------------------------------------------------------

  cancel(): void {
    this.stopPolling();
    this.loading = false;

    this.router.navigate([
      "/secure-blockchain/auth-tools/smart-contract/search"
    ]);
  }

  // ---------------------------------------------------------
  // DIALOGS
  // ---------------------------------------------------------

  openSuccessDialog(): void {
    const dialogRef = this.dialog.open(SuccessDialogComponent, {
      width: "250px",
      data: {
        isCreate: this.isCreate,
        title: "Smart Contract"
      }
    });

    dialogRef.afterClosed().subscribe(() => {
      this.router.navigate([
        "/secure-blockchain/auth-tools/smart-contract/search"
      ]);
    });
  }

  openErrorDialog(message: string): void {
    const dialogRef = this.dialog.open(ErrorDialogComponent, {
      width: "350px",
      data: {
        errorMessage: message
      }
    });

    dialogRef.afterClosed().subscribe(() => {
      this.router.navigate([
        "/secure-blockchain/auth-tools/smart-contract/search"
      ]);
    });
  }
}